import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import Select from "react-select";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import _ from "lodash";
import EditLocationModal from "../locations/EditLocationModal";
import "./editDivOfWorkStyles.css";
import locationActions from "../../../../_actions/locationActions";
import bidItemActions from "../../../../_actions/bidItemActions";
import AdminItemHelpModal from "./AdminItemHelpModal";

export default function EditDivOfWorkModalV3(props) {
  const { bidItemId, projectId, showModal, toggleModal } = props;

  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(locationActions.getAll(projectId));
  }, []);

  const fetching = useSelector(
    (state) => state.bidItems.fetching || state.locations.fetching || false
  );
  const bidItemDetails = useSelector((state) =>
    state.bidItems.openBidItemDetails[projectId]
      ? state.bidItems.openBidItemDetails[projectId][bidItemId] || null
      : null
  );
  const projLocationList = useSelector(
    (state) => state.locations.projLocationLists[projectId] || null
  );

  const genLoc = useMemo(
    () => (projLocationList || []).find((l) => l.isGeneric) || null,
    [projLocationList]
  );

  const [iblList, setIblList] = useState(
    (bidItemDetails.locations || []).map((ibl, index) => ({
      bidItemId: ibl.bidItemId,
      locationId: ibl.locationId,
      isGenericLocation: genLoc && genLoc.locationId === ibl.locationId,
      roadwayEeQuantity: ibl.roadwayEeQuantity,
      roadwayCcoQuantity: ibl.roadwayCcoQuantity,
      structureEeQuantity: ibl.structureEeQuantity,
      structureCcoQuantity: ibl.structureCcoQuantity,
      // Ibl has a composite key, so iblList could become non-unique during edit.
      // Example is when multiple ibl rows are added for an item w/o any location
      // selected. This utility property will act as an indexer for locating data.
      index,
      isNew: false, // Used to disable existing location dropdowns
    }))
  );

  const [isAdministrativeItem, setIsAdministrativeItem] = useState(
    bidItemDetails ? bidItemDetails.isAdministrativeItem : false
  );

  const [showAdminItemHelp, setShowAdminItemHelp] = useState(false);
  const toggleAdminItemHelp = () => setShowAdminItemHelp(!showAdminItemHelp);

  const [showAddLocationModal, setShowAddLocationModal] = useState(false);
  const toggleModal_addLocation = () => setShowAddLocationModal(!showAddLocationModal);

  const accumulator = useCallback((accum, currVal) => accum + currVal, []);

  const totalInputEeQtyForItem = useMemo(() => {
    const validIbls = iblList.filter((ibl) => ibl.locationId);
    if (validIbls.length > 0)
      return validIbls
        .map((ibl) => ibl.roadwayEeQuantity + ibl.structureEeQuantity)
        .reduce(accumulator);
    return 0;
  }, [iblList]);

  const getNewIblEntry = useCallback(
    (index = 0) => ({
      index, // NOT length - 1; it hasn't been added to array yet
      bidItemId,
      locationId: 0,
      isGenericLocation: false, // genLoc is locked from editing, so always false
      roadwayEeQuantity: 0,
      structureEeQuantity: 0,
      roadwayCcoQuantity: 0,
      structureCcoQuantity: 0,
      isNew: true,
    }),
    []
  );

  const updateRoadwayEeQuantity = useCallback(
    (index, value) => {
      if (!iblList[index]) return;
      const _iblList = iblList.map((ibl) => ({ ...ibl }));

      // Update genLoc's quantity. Rules for ROADWAY are as follows:
      // (For STRUCTURE, see below)
      // r1. Take from (or give back to) genLoc's ROADWAY quantity
      // r2. If #1 causes overrun, set genLoc.rdwyQty to 0, and take from any
      //     available genLoc.strQty
      // r3. If #2 causes overrun of structure genLoc, set structure genLoc to 0,
      //     and save. Any overruns from this point are allowed, but item
      //     is flagged as an overrun - but such flagging is not within scope of
      //     this handler.
      const existRdwyEeQty = _iblList[index].roadwayEeQuantity; // From props

      const inputRdwyQty = parseFloat(+value || 0);
      _iblList[index].roadwayEeQuantity = +inputRdwyQty.toFixed(3);

      // Get the amount of overrun qty
      const overrunQty = totalInputEeQtyForItem - bidItemDetails.eeQuantity;
      const qtyChange = existRdwyEeQty - inputRdwyQty;

      const genLocIbl = _iblList.find((i) => i.isGenericLocation);
      if (!genLocIbl) return; // Do not update if no genLoc found

      let genLocStrEeQty = parseFloat(genLocIbl.structureEeQuantity.toFixed(3));
      let genLocRdwyEeQty = parseFloat(genLocIbl.roadwayEeQuantity.toFixed(3));

      // Rule #r1
      genLocRdwyEeQty += qtyChange - overrunQty;

      if (genLocRdwyEeQty < 0) {
        // Rule #r2
        genLocStrEeQty -= Math.abs(genLocRdwyEeQty);
        genLocRdwyEeQty = 0;

        // Rule #r3
        if (genLocStrEeQty < 0) genLocStrEeQty = 0;
      }

      genLocIbl.roadwayEeQuantity = +genLocRdwyEeQty.toFixed(3);
      genLocIbl.structureEeQuantity = +genLocStrEeQty.toFixed(3);

      setIblList(_iblList);
    },
    [iblList]
  );

  const updateStructureEeQuantity = useCallback(
    (index, value) => {
      if (!iblList[index]) return;
      const _iblList = iblList.map((ibl) => ({ ...ibl }));

      // Update genLoc's quantity. Rules for STRUCTURE are as follows:
      // (For ROADWAY, see above)
      // s1. Take from (or give back to) STRUCTURE's genLoc quantity
      // s2. If #1 causes overrun, set structure genLoc to 0, and take from any
      //     available ROADWAY genLoc quantity
      // s3. If #2 causes overrun of roadway genLoc, set roadway genLoc to 0,
      //     and save. Any overruns from this point are allowed, but item
      //     is flagged as an overrun - but such flagging is not within scope of
      //     this handler.
      // Note: ensuring that a genLoc exists for every item is the backend's resp.

      const inputStrQty = parseFloat(+value || 0); // Format input val
      const existStrEeQty = _iblList[index].structureEeQuantity; // Store exist val
      const qtyChange = existStrEeQty - inputStrQty; // Delta. Negative if input > exist
      const overrunQty = totalInputEeQtyForItem - bidItemDetails.eeQuantity;

      _iblList[index].structureEeQuantity = +inputStrQty.toFixed(3); // Update

      // Get the genLoc's ibl
      const genLocIbl = _iblList.find((i) => i.isGenericLocation);
      if (!genLocIbl) return;

      // If item isAdminItem, editing of genLoc.strEeQty is allowed (but not
      // genLoc.rdwyEeQty.) Take from, or give back to, genLoc.rdwyEeQty.
      // (This step is unique to structure)
      if (isAdministrativeItem && index === genLocIbl.index) {
        // If user inputs an overrun into genLoc's strEeQty, set rdwyEeQty to zero.
        // Otherwise, update rdwyEeQty based on qtyChange.
        genLocIbl.roadwayEeQuantity = _.round(
          Math.max(genLocIbl.roadwayEeQuantity + qtyChange - overrunQty, 0),
          3
        );
      } else {
        let genLocStrEeQty = +genLocIbl.structureEeQuantity.toFixed(3);
        let genLocRdwyEeQty = +genLocIbl.roadwayEeQuantity.toFixed(3);

        // Rule #s1: Apply the change to genLoc's strEeQty
        genLocStrEeQty += qtyChange - overrunQty;

        if (genLocStrEeQty < 0) {
          // Rule #s2: If genLoc's strEeQty overruns, "rob" from its rdwyEeQty
          genLocRdwyEeQty -= Math.abs(genLocStrEeQty);

          // And zero out the strEeQty, which is currently negative
          genLocStrEeQty = 0;

          // Rule #s3: Finally, if genLoc's rdwyEeQty can't handle the change either,
          // set rdwyEeQty to zero.
          if (genLocRdwyEeQty < 0) genLocRdwyEeQty = 0;
        }

        genLocIbl.structureEeQuantity = +genLocStrEeQty.toFixed(3);
        genLocIbl.roadwayEeQuantity = +genLocRdwyEeQty.toFixed(3);
      }
      setIblList(_iblList);
    },
    [iblList]
  );

  const updateLocation = useCallback(
    (index, locationId) => {
      if (!iblList[index]) return;
      const _iblList = iblList.map((ibl) => ({ ...ibl }));
      _iblList[index].locationId = locationId;
      setIblList(_iblList);
    },
    [iblList]
  );

  const checkLocationLinks = useCallback((locationId) => {
    const check = async (locationId) =>
      await dispatch(bidItemActions.locationHasLinks(bidItemId, locationId));
    return check(locationId);
  }, []);

  const deleteIbl = useCallback(
    (index) => {
      let _iblList = iblList.map((ibl) => ({ ...ibl }));

      // Return Ibl's (strEeQty + rdwyEeQty) to genLoc's rdwyEeQty.
      // If there's an overrun, discard the overrun amount when returning.
      // (This is to force the return of overrun amount when deleting an ibl.)
      // If the (strEeQty + rdwyEeQty) of the ibl being deleted is less than
      // the overrun amount, nothing is returned.
      const genLocIbl = _iblList.find((ibl) => ibl.isGenericLocation);
      if (!genLocIbl) return;

      const strEeQtyToXfer = _iblList[index].structureEeQuantity;
      const rdwyEeQtyToXfer = _iblList[index].roadwayEeQuantity;

      // If there's no overrun, provide a simple x-fer to genLoc's rdwyEeQty
      if (totalInputEeQtyForItem <= bidItemDetails.eeQuantity) {
        genLocIbl.roadwayEeQuantity += _.round(rdwyEeQtyToXfer + strEeQtyToXfer, 3);
      } else {
        // Calculate the overrun amount.
        const overrunQty = totalInputEeQtyForItem - bidItemDetails.eeQuantity;
        genLocIbl.roadwayEeQuantity += _.round(
          Math.max(strEeQtyToXfer + rdwyEeQtyToXfer - overrunQty, 0),
          3
        );
      }

      // Perform deletion
      _iblList = [..._iblList.slice(0, index), ..._iblList.slice(index + 1)];

      // If no more ibl's left, push a blank one (but a genLoc will always be
      // present, so this theoretically will never be necessary).
      if (_iblList.length === 0) _iblList.push(getNewIblEntry());

      setIblList(_iblList);
    },
    [iblList]
  );

  const addIbl = useCallback(() => {
    const _iblList = iblList.map((ibl) => ({ ...ibl }));
    const newIndex = Math.max(..._iblList.map((ibl) => ibl.index)) + 1;
    _iblList.push(getNewIblEntry(newIndex));
    setIblList(_iblList);
  }, [iblList]);

  const handleChange_isAdministrativeItem = (e) => {
    if (e.target.checked) {
      const _iblList = iblList.map((ibl) => ({ ...ibl }));

      // X-fer all non-genLoc Q's to genLoc. Any overrunQty: discard, and assign all
      // eeQty to rdwyEeQty.
      const overrunQty = totalInputEeQtyForItem - bidItemDetails.eeQuantity;
      const genLoc = _iblList.find((ibl) => ibl.isGenericLocation);
      if (genLoc) {
        // If overrun, discard the extra and x-fer EE only
        if (overrunQty > 0) {
          genLoc.roadwayEeQuantity = +bidItemDetails.eeQuantity.toFixed(3);
          genLoc.structureEeQuantity = 0;
        }

        // Loop thru all non-generic ibl's
        _iblList
          .filter((ibl) => !ibl.isGenericLocation)
          .forEach((ibl) => {
            // If there's no overrun, x-fer to genLoc. No x-fer if overrun.
            if (overrunQty <= 0) {
              genLoc.roadwayEeQuantity += ibl.roadwayEeQuantity;
              genLoc.structureEeQuantity += ibl.structureEeQuantity;

              genLoc.roadwayEeQuantity = +genLoc.roadwayEeQuantity.toFixed(3);
              genLoc.structureEeQuantity = +genLoc.structureEeQuantity.toFixed(3);
            }

            ibl.roadwayEeQuantity = 0;
            ibl.structureEeQuantity = 0;
          });
      }

      setIblList(_iblList);
    }

    setIsAdministrativeItem(e.target.checked);
  };

  const handleClick_save = async () => {
    const _bidItemDetails = {
      ...bidItemDetails,
      isAdministrativeItem,
      locations: iblList
        .filter((ibl) => ibl.locationId)
        .map((_ibl) => ({
          bidItemId,
          locationId: _ibl.locationId,
          roadwayEeQuantity: _ibl.roadwayEeQuantity,
          roadwayCcoQuantity: _ibl.roadwayCcoQuantity,
          structureEeQuantity: _ibl.structureEeQuantity,
          structureCcoQuantity: _ibl.structureCcoQuantity,
        })),
    };

    await dispatch(bidItemActions.update(_bidItemDetails));
    toggleModal();
  };

  const closeBtn = (
    <button className="close" onClick={toggleModal}>
      <FontAwesomeIcon icon="times" className="fa-fw text-white" />
    </button>
  );

  return (
    <React.Fragment>
      <Modal isOpen={showModal} size={"xl"} fade={false}>
        <ModalHeader className="bg-info text-white" close={closeBtn}>
          <FontAwesomeIcon icon="edit" className="fa-fw mr-2" />
          Edit Location Assignments
        </ModalHeader>
        <ModalBody>
          <div className="card m-2">
            <div className="card-body p-2">
              <div className="form-row form-group pt-0 pb-2 mb-0">
                <div className="custom-control-inline custom-control custom-checkbox ml-2 mr-1">
                  <input
                    type="checkbox"
                    name="isAdministrativeItem"
                    id="isAdministrativeItem"
                    className="custom-control-input my-auto"
                    checked={isAdministrativeItem}
                    onChange={handleChange_isAdministrativeItem}
                  />
                  <label
                    htmlFor="isAdministrativeItem"
                    className="custom-control-label my-auto"
                  >
                    Manage quantities for General location only
                  </label>
                </div>
                <button
                  type="button"
                  name="adminItemHelp"
                  id="adminItemHelp"
                  className="btn btn-sm btn-secondary py-0 px-1 ml-2 mr-1 align-middle"
                  onClick={toggleAdminItemHelp}
                >
                  <FontAwesomeIcon icon="info-circle" className="fa-fw" />
                </button>
              </div>
              <h6 className="text-muted my-0 py-2 border-top">
                Breakdown by Location (Unit: {bidItemDetails.unitOfMeasure})
              </h6>
              <div className="d-flex flex-row w-100">
                <div className="alert alert-info p-2 w-100 mb-2">
                  <h6>
                    <FontAwesomeIcon icon="info-circle" className="fa-fw mr-2" />
                    Assigning/Unassigning Locations
                  </h6>
                  <ul className="mb-0">
                    <li>
                      Assign locations to this item by clicking on the "Assign New
                      Location" button. If you don't see the location you're looking for
                      in the location dropdown, click on the '
                      <FontAwesomeIcon
                        icon="plus"
                        className="fa-fw"
                        style={{ fontSize: "0.8rem" }}
                      />
                      ' button to add a new location.
                    </li>
                    <li>
                      Unassign locations from this item by clicking on the red '
                      <FontAwesomeIcon
                        icon="times"
                        className="fa-fw"
                        style={{ fontSize: "0.8rem" }}
                      />
                      ' button. You cannot unassign locations with a gray lock, since it
                      is being referenced by other data (e.g., Daily Reports), or it is a
                      'General' location (General locations cannot be unassigned).
                    </li>
                  </ul>
                </div>
              </div>
              <EditDivOfWorkModalV3_Locations
                projectId={projectId}
                bidItemId={bidItemId}
                unitOfMeasure={bidItemDetails.unitOfMeasure}
                eeQuantity={bidItemDetails.eeQuantity}
                isAdministrativeItem={isAdministrativeItem}
                iblList={iblList}
                updateLocation={updateLocation}
                updateRoadwayEeQuantity={updateRoadwayEeQuantity}
                updateStructureEeQuantity={updateStructureEeQuantity}
                addIbl={addIbl}
                deleteIbl={deleteIbl}
                accumulator={accumulator}
                checkLocationLinks={checkLocationLinks}
                toggleModal_addLocation={toggleModal_addLocation}
              />
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <div className="form-row form-group">
            <button
              name="cancelIblButton"
              id="cancelIblButton"
              type="button"
              className="btn btn-secondary mr-2"
              onClick={toggleModal}
              disabled={fetching}
            >
              Cancel
            </button>
            <button
              className="btn btn-primary"
              name="save"
              id="save"
              type="button"
              onClick={handleClick_save}
              disabled={fetching}
            >
              {fetching && (
                <span className="mr-2 mt-1">
                  <FontAwesomeIcon icon="spinner" style={{ color: "white" }} spin />
                </span>
              )}
              <b>{`${fetching ? "Saving" : "Save"}`}</b>
            </button>
          </div>
        </ModalFooter>
      </Modal>
      {showAdminItemHelp && (
        <AdminItemHelpModal
          showAdminItemHelp={showAdminItemHelp}
          toggleAdminItemHelp={toggleAdminItemHelp}
        />
      )}
      {showAddLocationModal && (
        <EditLocationModal
          isNewLocation={true}
          locationId={null}
          projectId={projectId}
          showModal={showAddLocationModal}
          toggleModal={toggleModal_addLocation}
        />
      )}
    </React.Fragment>
  );
}

function EditDivOfWorkModalV3_Locations(props) {
  const {
    projectId,
    bidItemId,
    unitOfMeasure,
    eeQuantity,
    iblList,
    isAdministrativeItem,
    updateLocation,
    updateRoadwayEeQuantity,
    updateStructureEeQuantity,
    addIbl,
    deleteIbl,
    accumulator,
    checkLocationLinks,
    toggleModal_addLocation,
  } = props;

  const [locIdsToDisable, setLocIdsToDisable] = useState([]);
  const [locIdsWithLinks, setLocIdsWithLinks] = useState([]);

  useEffect(
    () =>
      setLocIdsToDisable(
        iblList.filter((ibl) => ibl.locationId).map((ibl) => ibl.locationId)
      ),
    [iblList]
  );

  const projLocationList = useSelector(
    (state) => state.locations.projLocationLists[projectId] || null
  );

  const locationOptions = useMemo(() => {
    return (projLocationList || []).map((l) => ({ value: l.locationId, label: l.name }));
  }, [projLocationList]);

  const totalStrQtyForItem = useMemo(() => {
    const validIbls = iblList.filter((ibl) => ibl.locationId);
    if (validIbls.length > 0) {
      var tot = validIbls.map((ibl) => +ibl.structureEeQuantity).reduce(accumulator);
      return +tot.toFixed(3);
    }
    return 0;
  }, [iblList]);

  const totalRdwyQtyForItem = useMemo(() => {
    const validIbls = iblList.filter((ibl) => ibl.locationId);
    if (validIbls.length > 0) {
      var tot = validIbls.map((ibl) => +ibl.roadwayEeQuantity).reduce(accumulator);
      return +tot.toFixed(3);
    }
    return 0;
  }, [iblList]);

  const handleClick_addIbl = (e) => {
    e.preventDefault();
    addIbl();
  };

  return (
    <React.Fragment>
      <div className="d-flex flex-row border-top border-bottom align-items center">
        <div className="p-1 ibl-loc">
          <small>
            <b>Location:</b>
          </small>
        </div>
        <div className="p-1 ibl-add"></div>
        <div className="p-1 ibl-rdwyqty">
          <small>
            <b>EE</b> (Rdwy)
          </small>
        </div>
        <div className="p-1 ibl-strqty">
          <small>
            <b>EE</b> (Str)
          </small>
        </div>
        <div className="p-1 ibl-rdwyqty">
          <small>
            <b>CO</b> (Rdwy)
          </small>
        </div>
        <div className="p-1 ibl-strqty">
          <small>
            <b>CO</b> (Str)
          </small>
        </div>
        <div className="p-1 ibl-portion-data text-right">
          <small>% Alloc</small>
        </div>
        <div className="p-1 ibl-delete"></div>
      </div>
      <div className="ibl-container border-bottom mb-1">
        {iblList.length > 0 ? (
          iblList.map((ibl) => (
            <EditDivOfWorkModalV3_LocationRow
              key={`iblKey_${ibl.index}`}
              ibl={ibl}
              isAdministrativeItem={isAdministrativeItem}
              updateLocation={updateLocation}
              updateRoadwayEeQuantity={updateRoadwayEeQuantity}
              updateStructureEeQuantity={updateStructureEeQuantity}
              deleteIbl={deleteIbl}
              locationOptions={locationOptions}
              locIdsToDisable={locIdsToDisable}
              locIdsWithLinks={locIdsWithLinks}
              totalRdwyQtyForItem={totalRdwyQtyForItem}
              totalStrQtyForItem={totalStrQtyForItem}
              checkLocationLinks={checkLocationLinks}
              toggleModal_addLocation={toggleModal_addLocation}
            />
          ))
        ) : (
          <div className="d-flex f-flex row ibl-row ml-2">
            (Location subdivision does not exist for this item.)
          </div>
        )}
        <EditDivOfWorkModalV3_LocationTotalRow
          iblList={iblList}
          totalRdwyQtyForItem={totalRdwyQtyForItem}
          totalStrQtyForItem={totalStrQtyForItem}
          unitOfMeasure={unitOfMeasure}
          eeQuantity={eeQuantity}
        />
      </div>
      <div className="form-row form-group mb-0 ml-1 justify-content-start">
        <button
          type="button"
          name="addIbl_item"
          id="addIbl_item"
          className="btn btn-secondary align-middle"
          onClick={handleClick_addIbl}
        >
          <FontAwesomeIcon icon="stream" className="fa-fw mr-1" />
          Assign New Location
        </button>
      </div>
    </React.Fragment>
  );
}

function EditDivOfWorkModalV3_LocationRow(props) {
  const {
    ibl,
    isAdministrativeItem,
    updateLocation,
    updateRoadwayEeQuantity,
    updateStructureEeQuantity,
    deleteIbl,
    locationOptions,
    locIdsToDisable,
    locIdsWithLinks,
    totalRdwyQtyForItem,
    totalStrQtyForItem,
    checkLocationLinks,
    toggleModal_addLocation,
  } = props;

  const [selectedLocation, selectLocation] = useState(
    (locationOptions || []).find((lo) => lo.value === ibl.locationId) || null
  );
  useEffect(() => {
    selectLocation(
      (locationOptions || []).find((lo) => lo.value === ibl.locationId) || null
    );
  }, [locationOptions]);

  const [_roadwayEeQuantity, setRoadwayEeQuantity] = useState(
    ibl.roadwayEeQuantity.toString()
  );
  const [_roadwayCcoQuantity, setRoadwayCcoQuantity] = useState(
    ibl.roadwayCcoQuantity.toString()
  );
  const [_structureEeQuantity, setStructureEeQuantity] = useState(
    ibl.structureEeQuantity.toString()
  );
  const [_structureCcoQuantity, setStructureCcoQuantity] = useState(
    ibl.structureCcoQuantity.toString()
  );

  useEffect(() => {
    if (ibl.isGenericLocation || (!ibl.isGenericLocation && isAdministrativeItem)) {
      setRoadwayEeQuantity(ibl.roadwayEeQuantity.toString());
      setStructureEeQuantity(ibl.structureEeQuantity.toString());
    }
  }, [ibl]);

  const locPercent = useMemo(
    () =>
      Math.round(
        ((ibl.structureEeQuantity + ibl.roadwayEeQuantity) /
          (totalRdwyQtyForItem + totalStrQtyForItem)) *
          100
      ),
    [ibl]
  );

  const handleChange_location = (selected) => {
    updateLocation(ibl.index, selected ? selected.value : 0);
    selectLocation((locationOptions || []).find((lo) => lo.value === selected.value));
  };

  const handleClick_addLocation = (e) => {
    e.preventDefault();
    console.log("toggling");
    toggleModal_addLocation();
  };

  const handleClick_deleteIbl = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    // First check if the location has links. (Link exists if a DR with current
    // bid item has a LocationWorkPerformed with the selected locationId.)
    const locHasLinks = await checkLocationLinks(ibl.locationId);
    if (!locHasLinks) deleteIbl(ibl.index);
  };

  return (
    <div className="d-flex flex-row ibl-row align-items-center">
      <div className="p-1 ibl-loc">
        <Select
          name="locationSelect"
          id={`iblLocation_${ibl.index}`}
          className="flex-fill"
          value={selectedLocation}
          options={locationOptions}
          onChange={(selected) => handleChange_location(selected)}
          isOptionDisabled={(option) => (locIdsToDisable || []).includes(option.value)}
          menuPosition="auto"
          isDisabled={!ibl.isNew}
        />
      </div>
      <div className="p-1 ibl-add">
        <button
          type="button"
          name="addLocation"
          id={`addLocation_${ibl.index}`}
          className="btn px-2 btn-outline-secondary align-middle"
          onClick={handleClick_addLocation}
          disabled={!ibl.isNew}
          hidden={!ibl.isNew}
        >
          <FontAwesomeIcon icon="plus" className="fa-fw" />
        </button>
      </div>
      <div className="p-1 ibl-rdwyqty">
        <input
          type="number"
          name="roadwayEeQuantity"
          id={`iblRdwyEeQty_${ibl.index}`}
          className="form-control"
          value={_roadwayEeQuantity}
          onChange={(e) => setRoadwayEeQuantity(e.target.value)}
          onBlur={(e) => updateRoadwayEeQuantity(ibl.index, e.target.value)}
          disabled={
            !ibl.locationId ||
            ibl.isGenericLocation ||
            (!ibl.isGenericLocation && isAdministrativeItem)
          }
        />
      </div>
      <div className="p-1 ibl-strqty">
        <input
          type="number"
          name="structureEeQuantity"
          id={`iblStrEeQty_${ibl.index}`}
          className="form-control"
          value={_structureEeQuantity}
          onChange={(e) => setStructureEeQuantity(e.target.value)}
          onBlur={(e) => updateStructureEeQuantity(ibl.index, e.target.value)}
          disabled={
            !ibl.locationId ||
            (ibl.isGenericLocation && !isAdministrativeItem) ||
            (!ibl.isGenericLocation && isAdministrativeItem)
          }
        />
      </div>
      <div className="p-1 ibl-rdwyqty">
        <input
          type="number"
          name="roadwayCcoQuantity"
          id={`iblRdwyCcoQty_${ibl.index}`}
          className="form-control"
          value={_roadwayCcoQuantity}
          onChange={(e) => setRoadwayCcoQuantity(e.target.value)}
          onBlur={(e) => update(ibl.index, e.target.name, e.target.value)}
          disabled
        />
      </div>
      <div className="p-1 ibl-strqty">
        <input
          type="number"
          name="structureCcoQuantity"
          id={`iblStrCcoQty_${ibl.index}`}
          className="form-control"
          value={_structureCcoQuantity}
          onChange={(e) => setStructureCcoQuantity(e.target.value)}
          onBlur={(e) => update(ibl.index, e.target.name, e.target.value)}
          disabled
        />
      </div>
      <div className="p-1 ibl-portion-data text-right">{`${locPercent}%`}</div>
      <div className="p-1 ibl-delete">
        <div className="form-row form-group mb-0 justify-content-end">
          <button
            type="button"
            name="deleteIbl"
            id={`iblDelete_${ibl.index}`}
            className={`btn btn-sm p-1 align-middle ${
              ibl.isGenericLocation || (locIdsWithLinks || []).includes(ibl.locationId)
                ? "btn-outline-secondary"
                : "btn-outline-danger"
            }`}
            onClick={handleClick_deleteIbl}
            disabled={
              ibl.isGenericLocation || (locIdsWithLinks || []).includes(ibl.locationId)
            }
          >
            <FontAwesomeIcon
              icon={
                ibl.isGenericLocation || (locIdsWithLinks || []).includes(ibl.locationId)
                  ? "lock"
                  : "times"
              }
              className="fa-fw"
            />
          </button>
        </div>
      </div>
    </div>
  );
}

function EditDivOfWorkModalV3_LocationTotalRow(props) {
  const { unitOfMeasure, totalRdwyQtyForItem, totalStrQtyForItem, eeQuantity } = props;

  const overrunPercent = useMemo(
    () => Math.round(((totalRdwyQtyForItem + totalStrQtyForItem) / eeQuantity) * 100),
    [totalRdwyQtyForItem, totalStrQtyForItem, eeQuantity]
  );

  return (
    <div className="d-flex flex-row ibl-row align-items-center border-top">
      <div className="p-1 ibl-loc text-right">
        <small>
          <b>Total:</b>
        </small>
      </div>
      <div className="p-1 ibl-add"></div>
      <div className="p-1 ibl-rdwyqty text-right">
        {totalRdwyQtyForItem || 0} {unitOfMeasure}
      </div>
      <div className="p-1 ibl-strqty text-right">
        {totalStrQtyForItem || 0} {unitOfMeasure}
      </div>
      <div className="p-1 ibl-rdwyqty text-right"></div>
      <div className="p-1 ibl-strqty text-right"></div>
      <div className="p-1 ibl-portion-data"></div>
      <div className="p-1 ibl-delete text-right">
        {overrunPercent > 100 ? (
          <span className="font-weight-bold text-danger">{overrunPercent}% of EE</span>
        ) : (
          <span className="font-weight-bold">{overrunPercent}% of EE</span>
        )}
      </div>
    </div>
  );
}
